import React, { useEffect, createContext, useState, useRef } from "react";
import { parseCookies, setCookie } from "nookies";
import jwt_decode from "jwt-decode";
import ChatService from "../services/ChatServices";
import { setupOneSignalUser } from "../onesignalSetup";
import { GetSettings } from "../services/AccountServices";
import { getUserPermissions } from "../screens/Users/services/UserServicesV2";

const UserContext = createContext();

export function UserProvider({ children, userName, setIsLoading }) {
  const { token: _token } = parseCookies();
  const { ATENDFLOW_AID: account_id } = parseCookies();

  // Estados principais
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(_token || null);
  const [logged, setLogged] = useState(false);
  const [isInitializing, setIsInitializing] = useState(true); // NOVO: Estado de inicialização
  const [bottyData, setBottyData] = useState();
  const [accountLoading, setAccountLoading] = useState(true);

  // Estados para permissões
  const [userPermissions, setUserPermissions] = useState(null);
  const [permissionsLoading, setPermissionsLoading] = useState(true);
  const [permissionsError, setPermissionsError] = useState(null);

  const userPlan = useRef("");
  const settings = useRef();

  // Constantes de cache
  const PERMISSIONS_CACHE_KEY = "user_permissions";

  // Função para obter configurações da conta
  const getAccountSettings = async () => {
    try {
      settings.current = await GetSettings();
      if (settings.current) {
        localStorage.setItem("_ACCSETTINGS_", JSON.stringify(settings.current));
      }
    } catch (error) {
      console.error("❌ Erro ao carregar configurações da conta:", error);
    }
  };

  const superuser = {
    1: "Admin",
    2: "Gerente",
    3: "Supervisor",
    4: "Atendente",
  };

  // Funções de cache
  const getCachedPermissions = (userId) => {
    if (!userId) return null;

    try {
      const cached = sessionStorage.getItem(
        `${PERMISSIONS_CACHE_KEY}_${userId}`
      );
      if (!cached) return null;

      const data = JSON.parse(cached);
      return data;
    } catch {
      return null;
    }
  };

  const setCachedPermissions = (userId, permissions, minutes) => {
    if (!userId || !permissions) return;

    const expiry = Date.now() + minutes * 60 * 1000;
    const data = { permissions, expiry };

    try {
      sessionStorage.setItem(
        `${PERMISSIONS_CACHE_KEY}_${userId}`,
        JSON.stringify(data)
      );
    } catch (error) {
      console.warn("Erro ao salvar permissões no cache:", error);
    }
  };

  const isExpired = (cached) => {
    return Date.now() > cached.expiry;
  };

  const clearPermissionsCache = (userId) => {
    if (userId) {
      sessionStorage.removeItem(`${PERMISSIONS_CACHE_KEY}_${userId}`);
    }
  };

  // Função para carregar permissões do usuário
  const loadUserPermissions = async (force = false) => {
    if (!user?.user_id) {
      setPermissionsLoading(false);
      return;
    }

    try {
      setPermissionsLoading(true);
      setPermissionsError(null);

      // Verificar cache primeiro (se não for forçado)
      if (!force) {
        const cached = getCachedPermissions(user.user_id);
        if (cached && !isExpired(cached)) {
          console.log("✅ Permissões carregadas do cache:", cached.permissions);
          setUserPermissions(cached.permissions);
          setPermissionsLoading(false);
          return;
        }
      }

      // Carregar da API
      console.log("🔄 Carregando permissões da API...");
      const response = await getUserPermissions();
      const permissions = response.data.permissions;

      console.log("✅ Permissões carregadas da API:", permissions);

      // Atualizar estado
      setUserPermissions(permissions);

      // Cache por 30 minutos
      setCachedPermissions(user.user_id, permissions, 30);
    } catch (error) {
      console.error("❌ Erro ao carregar permissões:", error);
      setPermissionsError(error);

      // Fallback: usar permissões básicas
      setUserPermissions(null);
    } finally {
      setPermissionsLoading(false);
    }
  };

  // Função para verificar permissões do usuário
  const hasPermission = (moduleName, action = "read") => {
    if (!user || !moduleName) {
      console.log("❌ hasPermission: user ou moduleName não definido", {
        user: !!user,
        moduleName,
      });
      return false;
    }

    // Admin sempre tem acesso total
    if (user.superuser_level === 1) {
      console.log("✅ hasPermission: Admin tem acesso total");
      return true;
    }

    // 1ª Prioridade: Permissões detalhadas da API
    if (userPermissions && userPermissions[moduleName]) {
      const hasAccess = userPermissions[moduleName][action] || false;
      console.log(
        `🔍 hasPermission: ${moduleName}.${action} = ${hasAccess} (da API)`
      );
      return hasAccess;
    }

    // 2ª Prioridade: Para usuários level=0, APENAS usar permissões da API
    // Se não há permissões da API, negar acesso
    if (user.superuser_level === 0) {
      console.log(
        "❌ hasPermission: Usuário level=0 sem permissões específicas da API"
      );
      return false;
    }

    // 3ª Prioridade: Permissões básicas por nível (apenas para levels > 0)
    if (user.superuser_level > 0) {
      const hasAccess = checkBasicPermissionsByLevel(
        user.superuser_level,
        moduleName,
        action
      );
      console.log(
        `🔍 hasPermission: ${moduleName}.${action} = ${hasAccess} (fallback level ${user.superuser_level})`
      );
      return hasAccess;
    }

    console.log("❌ hasPermission: Negando acesso por padrão");
    return false;
  };

  // Função auxiliar para permissões básicas por nível
  const checkBasicPermissionsByLevel = (level, moduleName, action) => {
    const basicPermissions = {
      2: {
        // Gerente
        "Base de Clientes": {
          create: true,
          read: true,
          update: true,
          delete: false,
        },
        Chat: { create: true, read: true, update: true, delete: false },
        Agenda: { create: true, read: true, update: true, delete: false },
        Templates: { create: true, read: true, update: true, delete: false },
        Automações: { create: true, read: true, update: true, delete: false },
        "Meu Perfil": {
          create: false,
          read: true,
          update: true,
          delete: false,
        },
        Suporte: { create: true, read: true, update: false, delete: false },
        "Meus Agentes": {
          create: true,
          read: true,
          update: true,
          delete: false,
        },
        "Criar Agente": {
          create: true,
          read: true,
          update: true,
          delete: false,
        },
        "Pipeline CRM": {
          create: true,
          read: true,
          update: true,
          delete: false,
        },
        "Tags & Categorias": {
          create: true,
          read: true,
          update: true,
          delete: false,
        },
        WhatsApp: { create: false, read: true, update: true, delete: false },
        Publicações: { create: true, read: true, update: true, delete: false },
        "Minha Empresa": {
          create: false,
          read: true,
          update: true,
          delete: false,
        },
        Integrações: { create: false, read: true, update: true, delete: false },
        Personalizações: {
          create: false,
          read: true,
          update: true,
          delete: false,
        },
        Estatísticas: {
          create: false,
          read: true,
          update: false,
          delete: false,
        },
      },
      3: {
        // Supervisor
        "Base de Clientes": {
          create: true,
          read: true,
          update: true,
          delete: false,
        },
        Chat: { create: true, read: true, update: true, delete: false },
        Agenda: { create: true, read: true, update: true, delete: false },
        Templates: { create: true, read: true, update: true, delete: false },
        "Meu Perfil": {
          create: false,
          read: true,
          update: true,
          delete: false,
        },
        Suporte: { create: true, read: true, update: false, delete: false },
        "Pipeline CRM": {
          create: true,
          read: true,
          update: true,
          delete: false,
        },
        Estatísticas: {
          create: false,
          read: true,
          update: false,
          delete: false,
        },
      },
      4: {
        // Atendente
        Chat: { create: true, read: true, update: true, delete: false },
        "Base de Clientes": {
          create: true,
          read: true,
          update: true,
          delete: false,
        },
        Agenda: { create: true, read: true, update: true, delete: false },
        "Meu Perfil": {
          create: false,
          read: true,
          update: true,
          delete: false,
        },
        Suporte: { create: true, read: true, update: false, delete: false },
      },
    };

    const userPermissions = basicPermissions[level];
    if (!userPermissions || !userPermissions[moduleName]) {
      return false;
    }

    return userPermissions[moduleName][action] || false;
  };

  // Função para verificar se pode criar usuários
  const canCreateUsers = () => {
    if (!user) return false;
    return user.is_superuser && user.superuser_level <= 3;
  };

  // Função para verificar se pode gerenciar permissões
  const canManagePermissions = () => {
    if (!user) return false;
    return user.is_superuser && user.superuser_level <= 2;
  };

  // Função para verificar se pode criar usuário com determinado nível
  const canCreateUserWithLevel = (targetLevel) => {
    if (!user) return false;

    // Admin pode criar qualquer nível
    if (user.superuser_level === 1) return true;

    // Gerente pode criar supervisor e atendente
    if (user.superuser_level === 2) return [3, 4].includes(targetLevel);

    // Supervisor pode criar apenas atendente
    if (user.superuser_level === 3) return targetLevel === 4;

    return false;
  };

  // Função para obter módulos disponíveis do usuário
  const getAvailableModules = () => {
    if (!user) return [];

    // Admin tem acesso a todos
    if (user.superuser_level === 1) {
      return [
        "Dashboard",
        "Chat",
        "Agenda",
        "Meus Agentes",
        "Base de Clientes",
        "Templates",
        "WhatsApp",
        "Campanhas",
        "Automações",
        "CRM",
        "Configurações",
        "Tags",
        "Usuários",
        "Meu Perfil",
        "Suporte",
      ];
    }

    // Se temos permissões detalhadas, usar módulos disponíveis delas
    if (userPermissions) {
      return Object.keys(userPermissions).filter(
        (moduleName) => userPermissions[moduleName].read === true
      );
    }

    // Fallback: módulos baseados no nível
    const modulesByLevel = {
      2: [
        "Dashboard",
        "Chat",
        "Agenda",
        "Base de Clientes",
        "Templates",
        "Automações",
        "CRM",
        "Tags",
        "Usuários",
        "Meu Perfil",
        "Meus Agentes",
        "Pipeline CRM",
        "WhatsApp",
        "Publicações",
      ],
      3: [
        "Dashboard",
        "Chat",
        "Agenda",
        "Base de Clientes",
        "Templates",
        "CRM",
        "Usuários",
        "Meu Perfil",
      ],
      4: ["Chat", "Agenda", "Base de Clientes", "Meu Perfil"],
    };

    return modulesByLevel[user.superuser_level] || [];
  };

  // Função para atualizar permissões (útil após alterações)
  const refreshPermissions = () => {
    if (user?.user_id) {
      clearPermissionsCache(user.user_id);
      loadUserPermissions(true);
    }
  };

  // Função para buscar conta WhatsApp
  const fetchWaAccount = async (token) => {
    try {
      console.log("🔄 Carregando conta WhatsApp...");
      const account = await ChatService.getAccount(token);
      if (account.status === 200 && account.data.length > 0) {
        let data = account.data[0];
        let createdAt = new Date(data.created * 1000);
        createdAt.setHours(createdAt.getHours() + 3);
        createdAt = createdAt.toLocaleString();

        data.token = token;
        const finalData = { ...data, createdAt };
        sessionStorage.setItem("botty_account", JSON.stringify(finalData));
        setBottyData(finalData);
        console.log("✅ Conta WhatsApp carregada:", finalData);
      } else {
        setBottyData({ token: token });
        console.log("⚠️ Nenhuma conta WhatsApp encontrada");
      }
    } catch (error) {
      console.error("❌ Erro ao carregar conta WhatsApp:", error);
      setBottyData({ token: token });
    } finally {
      setAccountLoading(false);
    }
  };

  // UseEffect principal - MODIFICADO para incluir estado de inicialização
  useEffect(() => {
    const initializeUser = async () => {
      console.log("🔄 Iniciando inicialização do usuário...");

      try {
        if (_token) {
          console.log("🔍 Token encontrado, decodificando...");

          let _user = jwt_decode(_token);

          // Limpar campos desnecessários
          delete _user.access;
          delete _user.jti;
          delete _user.exp;
          delete _user.token_type;

          console.log("✅ Token decodificado com sucesso:", _user);

          // Adicionar propriedades de permissão se não existirem
          if (!_user.hasOwnProperty("can_create_users")) {
            _user.can_create_users =
              _user.is_superuser && _user.superuser_level <= 3;
          }

          if (!_user.hasOwnProperty("can_manage_permissions")) {
            _user.can_manage_permissions =
              _user.is_superuser && _user.superuser_level <= 2;
          }

          userName.current = _user?.first_name;

          // Definir estados do usuário
          setUser(_user);
          setToken(_token);
          setLogged(true);

          console.log("✅ Estados do usuário definidos");

          // Carregar configurações da conta (não bloqueante)
          if (!settings.current) {
            getAccountSettings().catch((error) => {
              console.error("❌ Erro ao carregar configurações:", error);
            });
          }

          // Definir cookie da conta se não existir
          if (!account_id) {
            setCookie(undefined, "ATENDFLOW_AID", _user.account_id, {
              maxAge: 60 * 60 * 24 * 7, // 7 dias
            });
            console.log("✅ Cookie da conta definido");
          }

          // Carregar conta WhatsApp se houver token (não bloqueante)
          if (_user.botty_token) {
            fetchWaAccount(_user.botty_token).catch((error) => {
              console.error("❌ Erro ao carregar conta WhatsApp:", error);
            });
          }

          setIsLoading(false)
        } else {
          console.log("❌ Nenhum token encontrado - usuário não logado");
          setLogged(false);
          setUser(null);
          setToken(null);
        }
      } catch (error) {
        console.error("❌ Erro durante inicialização do usuário:", error);
        setLogged(false);
        setUser(null);
        setToken(null);
      } finally {
        // IMPORTANTE: Marcar inicialização como completa SEMPRE
        console.log("✅ Inicialização do usuário completa");
        setIsInitializing(false);
      }
    };

    // Executar inicialização
    initializeUser();
  }, [_token]);

  // UseEffect para carregar permissões quando usuário for definido
  useEffect(() => {
    if (user?.user_id && logged) {
      console.log("🔄 Carregando permissões do usuário...");
      loadUserPermissions();
    } else {
      console.log("⚠️ Usuário não logado, não carregando permissões");
      setPermissionsLoading(false);
      setUserPermissions(null);
    }
  }, [user?.user_id, logged]);

  // Debug: Log do estado atual
  useEffect(() => {
    console.log("📊 Estado atual do UserContext:", {
      isInitializing,
      logged,
      user: user ? { id: user.user_id, level: user.superuser_level } : null,
      permissionsLoading,
      userPermissions: userPermissions ? Object.keys(userPermissions) : null,
    });
  }, [isInitializing, logged, user, permissionsLoading, userPermissions]);

  return (
    <UserContext.Provider
      value={{
        // Estados principais
        user,
        setUser,
        logged,
        setLogged,
        token,
        setToken,
        isInitializing, // NOVO: Estado de inicialização

        superuser,
        userPlan,
        bottyData,
        setBottyData,
        fetchWaAccount,
        settings,
        accountLoading,
        setAccountLoading,

        // Estados de permissões
        userPermissions,
        permissionsLoading,
        permissionsError,

        // Funções de permissão
        hasPermission,
        canCreateUsers,
        canManagePermissions,
        canCreateUserWithLevel,
        getAvailableModules,
        loadUserPermissions,
        refreshPermissions,
        clearPermissionsCache,
      }}
    >
      {children}
    </UserContext.Provider>
  );
}

export default UserContext;
